import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  type SetServiceBudget,
  useApproveServiceBudgetRequest,
  useRejectServiceBudgetRequest,
} from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import {
  currentAmount,
  parseBudget,
  requestedAmount,
  SERVICE_LABEL,
  unitOf,
} from "@/lib/services-gateway";

import type { PendingDecision } from "./types";

interface DecideDialogProps {
  decision: PendingDecision | null;
  onOpenChange: (open: boolean) => void;
}

export function DecideDialog({ decision, onOpenChange }: DecideDialogProps) {
  return (
    <Dialog open={decision !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {decision && (
          // Remount per decision so a note never carries into another request.
          <DecideForm
            key={`${decision.request.id}-${decision.action}`}
            decision={decision}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function DecideForm({ decision, onClose }: { decision: PendingDecision; onClose: () => void }) {
  const { request, action } = decision;
  const approve = useApproveServiceBudgetRequest();
  const reject = useRejectServiceBudgetRequest();
  const [note, setNote] = useState("");
  // Empty: grant what was asked for.
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const pending = approve.isPending || reject.isPending;
  const isApprove = action === "approve";
  const isUsd = unitOf(request.service) === "usd";
  const override = parseOverride(isUsd, amount);
  const canSubmit = !pending && (isApprove ? override !== null : note.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit || override === null) {
      return;
    }
    setError(null);
    try {
      if (isApprove) {
        await approve.mutateAsync({
          requestId: request.id,
          projectId: request.project_id,
          budget: override,
          note: note.trim() || undefined,
        });
        toast.success(`Budget set for ${request.project_name}.`);
      } else {
        await reject.mutateAsync({
          requestId: request.id,
          projectId: request.project_id,
          note: note.trim(),
        });
        toast.success("Request rejected.");
      }
      onClose();
    } catch (err) {
      logger.error(`[budget-requests] ${action} failed:`, err);
      setError(readApiErrorMessage(err, `Failed to ${action} the request. Please try again.`));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isApprove ? "Approve budget request" : "Reject budget request"}</DialogTitle>
        <DialogDescription>
          <strong>{request.project_name}</strong>, {SERVICE_LABEL[request.service]}:{" "}
          {requestedAmount(request)} (now {currentAmount(request)}), requested by{" "}
          {request.requester_email}.{" "}
          {isApprove
            ? "Approving sets the budget now; it applies from the project's next request."
            : "Rejecting is final; the requester can ask again later."}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="space-y-4">
          <div className="bg-muted/40 rounded-md p-3 text-sm">
            <p className="text-muted-foreground text-xs tracking-wider uppercase">Reason</p>
            <p className="mt-1">{request.reason}</p>
          </div>

          {isApprove && (
            <div className="space-y-1.5">
              <Label htmlFor="budget-override">
                {isUsd ? "Grant (USD a month)" : "Grant (requests a month)"}, empty: as asked
              </Label>
              <Input
                id="budget-override"
                inputMode={isUsd ? "decimal" : "numeric"}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={pending}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="budget-note">
              {isApprove ? "Note (optional)" : "Reason (required)"}
            </Label>
            <Textarea
              id="budget-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                isApprove ? "Anything the requester should know" : "What would need to change?"
              }
              rows={3}
              maxLength={2000}
              disabled={pending}
            />
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant={isApprove ? "default" : "destructive"}
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
        >
          {pending ? (isApprove ? "Setting…" : "Rejecting…") : isApprove ? "Approve" : "Reject"}
        </Button>
      </DialogFooter>
    </>
  );
}

/** The admin's amount, `{}` for "as asked", or `null` while it is not a valid one. */
function parseOverride(isUsd: boolean, text: string): SetServiceBudget | null {
  return text.trim() === "" ? {} : parseBudget(isUsd ? "usd" : "requests", text);
}
